// 🔧 CHECKOUTPAGE.JS - FIXES FOR:
// 1. Add retry logic when payment history is empty
// 2. Poll with exponential backoff
// 3. Max 5 attempts with 2-second delays
// 4. Show user feedback during polling

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { createCheckoutSession, getPaymentHistory, createSubscription } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { Loader2, CreditCard, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import PricingSection from './PricingSection';

const LoadingSpinner = () => (
  <div className="flex items-center justify-center">
    <Loader2 className="h-5 w-5 animate-spin" />
  </div>
);

// ===============================================
// 🔧 FIX: Success Page with Retry Logic
// ===============================================
const SuccessPage = () => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { organizationId } = useContext(OrganizationContext);
  const location = useLocation();
  const navigate = useNavigate();

  const MAX_RETRIES = 5;
  const RETRY_DELAY = 2000; // 2 seconds

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        if (!organizationId) {
          console.log('Waiting for organization ID...');
          return;
        }

        console.log(`Fetching payment history for organization: ${organizationId} (Attempt ${retryCount + 1}/${MAX_RETRIES})`);
        setLoading(true);
        setError(null);

        const paymentHistory = await getPaymentHistory(organizationId);
        console.log('Payment history response:', paymentHistory);

        // ✅ CHECK IF PAYMENT RECORD EXISTS
        if (!paymentHistory || paymentHistory.length === 0) {
          // Payment record doesn't exist yet (webhook hasn't processed)
          if (retryCount < MAX_RETRIES) {
            console.warn(`❌ No payments found. Retrying in ${RETRY_DELAY}ms (${retryCount + 1}/${MAX_RETRIES})...`);
            
            // Retry after delay
            const timer = setTimeout(() => {
              setRetryCount(retryCount + 1);
            }, RETRY_DELAY);

            return () => clearTimeout(timer);
          } else {
            // Exhausted all retries
            throw new Error(
              'Payment record not found after multiple retries. ' +
              'The payment was successful in Stripe, but the system is having trouble processing it. ' +
              'Please contact support with Organization ID: ' + organizationId
            );
          }
        }

        // ✅ PAYMENT RECORD FOUND
        const latestPayment = paymentHistory[0];
        console.log('✅ Latest payment found:', latestPayment);

        setPaymentDetails({
          sessionId: new URLSearchParams(location.search).get('session_id'),
          amount: latestPayment.amount,
          currency: latestPayment.currency || 'GBP',
          status: latestPayment.status,
          date: new Date(latestPayment.paymentDate).toLocaleDateString('en-GB'),
          paymentType: latestPayment.paymentType,
          referenceId: latestPayment.stripePaymentIntentId
        });

        setError(null);
        setRetryCount(0); // Reset on success
        setLoading(false);

      } catch (err) {
        console.error('❌ Error in fetchPaymentDetails:', err);
        setError(err.message || 'Failed to load payment details. Please contact support.');
        setLoading(false);
      }
    };

    // Only fetch when organizationId is available
    if (organizationId) {
      fetchPaymentDetails();
    }

  }, [organizationId, location.search, retryCount]);

  // ✅ LOADING STATE: Show spinner with retry info
  if (!organizationId || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <LoadingSpinner />
            <div>
              <p className="font-medium">Processing your payment...</p>
              <p className="text-sm text-muted-foreground mt-2">
                {retryCount > 0 
                  ? `Checking payment status... (${retryCount}/${MAX_RETRIES})`
                  : 'Please wait while we confirm your payment'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ✅ ERROR STATE
  if (error) {
    return (
      <Card className="max-w-md mx-auto mt-8">
        <CardHeader>
          <div className="flex items-center justify-center mb-4">
            <XCircle className="h-12 w-12 text-destructive" />
          </div>
          <CardTitle className="text-center">Error Loading Payment Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
          <div className="space-y-2">
            <Button 
              className="w-full"
              onClick={() => {
                setRetryCount(0);
                setLoading(true);
              }}
            >
              <Loader2 className="mr-2 h-4 w-4" />
              Retry
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/subscriptions')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              View Subscriptions
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ✅ SUCCESS STATE
  return (
    <Card className="max-w-md mx-auto mt-8">
      <CardHeader>
        <div className="flex items-center justify-center mb-4">
          <CheckCircle className="h-12 w-12 text-primary" />
        </div>
        <CardTitle className="text-center">Payment Successful! 🎉</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {paymentDetails && (
          <div className="space-y-4">
            <h3 className="font-semibold text-sm">Payment Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium">
                  {new Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: paymentDetails.currency
                  }).format(paymentDetails.amount)}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date</span>
                <span className="font-medium">{paymentDetails.date}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <span className="font-medium capitalize">
                  {paymentDetails.paymentType?.replace('_', ' ').toLowerCase()}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium text-primary capitalize">
                  {paymentDetails.status}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-muted-foreground">Reference</span>
                <span className="font-medium text-xs break-all">
                  {paymentDetails.referenceId?.substring(0, 20)}...
                </span>
              </div>
            </div>
          </div>
        )}
        
        <Alert>
          <AlertDescription className="text-sm">
            You will receive a confirmation email shortly.
          </AlertDescription>
        </Alert>
        
        <Button 
          className="w-full"
          onClick={() => navigate('/subscriptions')}
        >
          View My Subscriptions
        </Button>
      </CardContent>
    </Card>
  );
};

// ===============================================
// Failed Page
// ===============================================
const FailedPage = () => {
  const navigate = useNavigate();

  return (
    <Card className="max-w-md mx-auto mt-8">
      <CardHeader>
        <div className="flex items-center justify-center mb-4">
          <XCircle className="h-12 w-12 text-destructive" />
        </div>
        <CardTitle className="text-center">Payment Failed</CardTitle>
        <CardDescription className="text-center">
          Unfortunately, your payment was not successful.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <p className="font-medium">This could be due to:</p>
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            <li>Insufficient funds</li>
            <li>Expired card</li>
            <li>Invalid card details</li>
            <li>Bank declined transaction</li>
          </ul>
        </div>

        <p className="text-sm text-muted-foreground">
          Please try again or contact your bank for more information.
        </p>

        <div className="space-y-3">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate('/pricing')}
          >
            Try Again
          </Button>
          <Button 
            className="w-full"
            onClick={() => navigate('/subscriptions')}
          >
            View Subscriptions
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ===============================================
// Payment Form
// ===============================================
const PaymentForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { organizationId, isLoading: isOrgLoading, error: orgError } = 
    useContext(OrganizationContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const planData = localStorage.getItem('selectedPlan');
    if (planData) {
      setSelectedPlan(JSON.parse(planData));
    }
  }, []);

  const handleCheckout = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements || !selectedPlan || !organizationId) {
      setError('Missing required information. Please refresh and try again.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // ── Step 1: Save subscription record to DB BEFORE redirecting to Stripe ─
      // The webhook handler (checkout.session.completed) needs to find this
      // record in order to link the stripeSubscriptionId and create the payment.
      const subscriptionData = {
        subscriptionPlan:   selectedPlan.subscriptionPlan || selectedPlan.type?.toUpperCase(),
        subscriptionAmount: selectedPlan.subscriptionAmount || selectedPlan.price,
        subscriptionFreq:   selectedPlan.subscriptionFreq  || selectedPlan.billing?.toUpperCase(),
        coveredCallAmount:  selectedPlan.coveredCallAmount  || 0,
        priceId:            selectedPlan.priceId,
        productName:        selectedPlan.productName,
        description:        selectedPlan.description,
        startDate:          selectedPlan.startDate || new Date().toISOString().split('T')[0],
        isActive:           false,          // becomes true when webhook fires
        paymentStatus:      'PENDING',
      };

      console.log('Saving subscription before checkout:', subscriptionData);
      await createSubscription(organizationId, subscriptionData);
      console.log('✅ Subscription record saved — proceeding to Stripe checkout');

      // ── Step 2: Create Stripe checkout session and redirect ───────────────
      const { sessionId } = await createCheckoutSession(
        organizationId, 
        selectedPlan.priceId
      );

      const { error: stripeError } = await stripe.redirectToCheckout({ sessionId });
      if (stripeError) {
        throw new Error(stripeError.message);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to initiate checkout. Please try again.');
      setIsLoading(false);
    }
  };

  if (isOrgLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (orgError) {
    return (
      <Alert variant="destructive" className="max-w-md mx-auto mt-8">
        <AlertDescription>
          Error loading organization details. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  if (!organizationId) {
    return (
      <Card className="max-w-md mx-auto mt-8">
        <CardHeader>
          <CardTitle>Organization Required</CardTitle>
          <CardDescription>
            Please complete your organization setup first.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            className="w-full"
            onClick={() => navigate('/organization')}
          >
            Setup Organization
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!selectedPlan) {
    return (
      <div className="container mx-auto px-4">
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Select a Plan</CardTitle>
            <CardDescription>
              Please choose a plan to continue with checkout
            </CardDescription>
          </CardHeader>
        </Card>
        
        <PricingSection 
          onPlanSelected={(plan) => {
            setSelectedPlan(plan);
            localStorage.setItem('selectedPlan', JSON.stringify(plan));
          }}
          hideNavigation={true}
        />
      </div>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto mt-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-6 w-6" />
          Checkout Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="font-medium">Selected Plan Details</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <span className="text-muted-foreground">Plan Name</span>
              <p className="font-medium">
                {selectedPlan.type.charAt(0).toUpperCase() + selectedPlan.type.slice(1)} Plan
              </p>
            </div>
            <div className="space-y-2">
              <span className="text-muted-foreground">Billing Period</span>
              <p className="font-medium">
                {selectedPlan.billing.charAt(0).toUpperCase() + selectedPlan.billing.slice(1)}
              </p>
            </div>
            <div className="space-y-2">
              <span className="text-muted-foreground">Amount</span>
              <p className="font-medium">
                £{selectedPlan.subscriptionAmount}
              </p>
            </div>
            <div className="space-y-2">
              <span className="text-muted-foreground">Start Date</span>
              <p className="font-medium">
                {new Date(selectedPlan.startDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          className="w-full"
          onClick={handleCheckout}
          disabled={isLoading || !stripe}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Proceed to Payment'
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// ===============================================
// Main Checkout Page Component
// ===============================================
const CheckoutPage = () => {
  const location = useLocation();

  if (location.pathname.includes('/success')) {
    return <SuccessPage />;
  }

  if (location.pathname.includes('/canceled')) {
    return <FailedPage />;
  }

  return <PaymentForm />;
};

export default CheckoutPage;